/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Evgeniy Naumov
 * @version 1.22.0
 * @since 1.19.0
 */

import { Fragment, RefObject, useEffect, useRef } from 'react';

import { connect } from 'react-redux';
import { FixedSizeList } from 'react-window';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import EllipsisText from '@components/common/EllipsisText/EllipsisText';
import { formatNumberToPhone, PhoneFormatType } from '@components/common/PhoneMask/formatNumberToPhone';
import InfinityScrollWrapper from '@components/InfinityScrollWrapper/InfinityScrollWrapper';
import { usePreventWindowScroll } from '@modules/app/hooks/usePreventWindowScroll';
import {
    makeSelectTranslationWizardMultiSearch,
} from '@modules/internationalization/intSelectors';
import { IWizardMultiSearchTranslation } from '@modules/internationalization/types';
import { IRootReducer } from '@reducers/rootReducer';
import { stringFormatParams } from '@utils/stringFormat';

import { wizardMultiSearchGetSubscriberListAction } from '../actions';
import { IWizardMultiSearchReducer } from '../reducer';
import {
    makeSelectWizardMultiSearchStepConfirm,
    makeSelectWizardMultiSearchSubscriberList,
    makeSelectWizardMultiSearchSubscriberListError,
    makeSelectWizardMultiSearchSubscriberListHasMore,
    makeSelectWizardMultiSearchSubscriberListLoading,
} from '../selectors';
import { IWizardMultiSearchGetSubscriberListPayload, IWizardMultiSearchStepConfirm } from '../types';
import { WizardMultiSearchSubscribersStyled } from './styled/WizardMultiSearchSubscribersStyled';


const WizardMultiSearchStepConfirm = ({
                                          getSubscriberList,
                                          msisdn,
                                          msisdnError,
                                          msisdnHasMore,
                                          msisdnIsLoading,
                                          msisdnNotFound,
                                          msisdnNotFoundError,
                                          msisdnNotFoundHasMore,
                                          msisdnNotFoundIsLoading,
                                          stepData,
                                          wizardTranslation,
                                      }: IConnectedProps & IDispatchProps) => {

    const { ConfirmSelectionStep: tStep, error: tError } = wizardTranslation;

    const listFoundRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
    const listNotFoundRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);

    usePreventWindowScroll(listFoundRef);
    usePreventWindowScroll(listNotFoundRef);

    useEffect(() => {
        getSubscriberList({ isFound: true, start: true });
        getSubscriberList({ isFound: false, start: true });
    }, [getSubscriberList]);

    const onSubscriberListScroll = (isFound: boolean) => {
        getSubscriberList({
            isFound,
            start: false,
        });
    };

    const renderSubscriberList = (isFound: boolean) => {
        const error = isFound ? msisdnError : msisdnNotFoundError;

        if (error) return <div className="wizard-multi-search__text_error">
            {tError.defaultMessage}
        </div>;

        const hasMore = isFound ? msisdnHasMore : msisdnNotFoundHasMore;
        const isLoading = isFound ? msisdnIsLoading : msisdnNotFoundIsLoading;
        const list = isFound ? msisdn : msisdnNotFound;
        const ref = isFound ? listFoundRef : listNotFoundRef;


        return (
            <InfinityScrollWrapper
                hasMore={hasMore}
                isLoading={isLoading}
                sizeElement={ref}
                watchElement={ref}

                onScrollEvent={() => onSubscriberListScroll(isFound)}
            >
                <FixedSizeList
                    className="multi-search-subscribers__list"
                    height={list.length * 32 > 200 ? 200 : list.length * 32}
                    itemCount={list.length}
                    itemSize={25}
                    outerRef={ref}
                    width="100%"
                >
                    {({ index, style }) =>
                        <div key={list[index]} style={style}>
                            {isFound
                                ? formatNumberToPhone(list[index], PhoneFormatType.header)
                                : <EllipsisText title={list[index]}>{list[index]}</EllipsisText>
                            }
                        </div>
                    }
                </FixedSizeList>
            </InfinityScrollWrapper>
        );
    };

    return (
        <Fragment>
            <WizardMultiSearchSubscribersStyled>
                {
                    !!stepData.foundMsisdnsCount && (
                        <div data-name="wizard-multi-search-list-found">
                            <div className="multi-search-subscribers__title">
                                {stringFormatParams(tStep.subscribersFound, {
                                    from: stepData.foundMsisdnsCount,
                                    to: stepData.foundMsisdnsCount + stepData.notFoundMsisdnsCount,
                                })}
                            </div>
                            {renderSubscriberList(true)}
                        </div>
                    )
                }
                {
                    !!stepData.notFoundMsisdnsCount && (
                        <div data-name="wizard-multi-search-list-not-found">
                            <div className="multi-search-subscribers__title">
                                {stringFormatParams(tStep.subscribersNotFound, {
                                    from: stepData.notFoundMsisdnsCount,
                                    to: stepData.foundMsisdnsCount + stepData.notFoundMsisdnsCount,
                                })}
                            </div>
                            {renderSubscriberList(false)}
                        </div>
                    )
                }
            </WizardMultiSearchSubscribersStyled>
        </Fragment>
    );
};

interface IConnectedProps extends Pick<IWizardMultiSearchReducer,
    'msisdn' | 'msisdnHasMore' | 'msisdnIsLoading' | 'msisdnError'
    | 'msisdnNotFound' | 'msisdnNotFoundHasMore' | 'msisdnNotFoundIsLoading' | 'msisdnNotFoundError'> {

    stepData: IWizardMultiSearchStepConfirm;
    wizardTranslation: IWizardMultiSearchTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    msisdn: makeSelectWizardMultiSearchSubscriberList(),
    msisdnError: makeSelectWizardMultiSearchSubscriberListError(),
    msisdnHasMore: makeSelectWizardMultiSearchSubscriberListHasMore(),
    msisdnIsLoading: makeSelectWizardMultiSearchSubscriberListLoading(),
    msisdnNotFound: makeSelectWizardMultiSearchSubscriberList(false),
    msisdnNotFoundError: makeSelectWizardMultiSearchSubscriberListError(false),
    msisdnNotFoundHasMore: makeSelectWizardMultiSearchSubscriberListHasMore(false),
    msisdnNotFoundIsLoading: makeSelectWizardMultiSearchSubscriberListLoading(false),
    stepData: makeSelectWizardMultiSearchStepConfirm(),
    wizardTranslation: makeSelectTranslationWizardMultiSearch(),
});

export interface IDispatchProps {
    getSubscriberList: (payload: IWizardMultiSearchGetSubscriberListPayload) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    getSubscriberList: payload => dispatch(wizardMultiSearchGetSubscriberListAction(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(WizardMultiSearchStepConfirm);
